import {
    Box,
    Card,
    Stack,
    Skeleton,
    useTheme,
} from "@mui/material";

export const CalendarMonthSkeleton = () => {
    const theme = useTheme();

    const days = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
    const rows = 6;
    const cols = 7;

    return (
        <Card
            elevation={0}
            sx={{
                borderRadius: 4,
                p: 3,
                backgroundColor: "var(--fx-surface-base, #FFFFFF)",
                border: `1px solid ${theme.palette.divider}`,
            }}
        >
            <Stack spacing={3}>
                {/* Top header */}
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={2}
                >
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Skeleton variant="rounded" width={44} height={44} />
                        <Skeleton variant="text" width={220} height={42} />
                    </Stack>

                    <Stack direction="row" spacing={1}>
                        <Skeleton variant="rounded" width={72} height={36} />
                        <Skeleton variant="rounded" width={84} height={36} />
                        <Skeleton variant="rounded" width={72} height={36} />
                    </Stack>
                </Stack>

                {/* Month title / navigation */}
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mt: 1 }}
                >
                    <Skeleton variant="rounded" width={36} height={36} />

                    <Stack spacing={1} alignItems="center">
                        <Skeleton variant="text" width={120} height={20} />
                        <Skeleton variant="text" width={180} height={48} />
                    </Stack>

                    <Skeleton variant="rounded" width={36} height={36} />
                </Stack>

                {/* Action row */}
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack spacing={1}>
                        <Skeleton variant="text" width={90} height={18} />
                        <Skeleton variant="text" width={70} height={18} />
                    </Stack>

                    <Skeleton variant="rounded" width={154} height={40} />
                </Stack>

                {/* Calendar grid */}
                <Box
                    sx={{
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 3,
                        overflow: "hidden",
                    }}
                >
                    {/* Weekday header */}
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(7, 1fr)",
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            backgroundColor: "var(--fx-surface-sunken, #F8FAFC)",
                        }}
                    >
                        {days.map((day) => (
                            <Box
                                key={day}
                                sx={{
                                    px: 2,
                                    py: 1.5,
                                    borderRight: `1px solid ${theme.palette.divider}`,
                                    "&:last-of-type": { borderRight: "none" },
                                }}
                            >
                                <Skeleton variant="text" width={32} height={18} sx={{ mx: "auto" }} />
                            </Box>
                        ))}
                    </Box>

                    {/* Month cells */}
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: `repeat(${cols}, 1fr)`,
                        }}
                    >
                        {Array.from({ length: rows * cols }).map((_, index) => (
                            <Box
                                key={index}
                                sx={{
                                    minHeight: 110,
                                    p: 1.5,
                                    borderRight: `1px solid ${theme.palette.divider}`,
                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                    position: "relative",
                                    "&:nth-of-type(7n)": { borderRight: "none" },
                                }}
                            >
                                {/* day number */}
                                <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                                    <Skeleton variant="text" width={18} height={24} />
                                </Box>

                                {/* fake event blocks */}
                                <Stack spacing={1}>
                                    <Skeleton variant="rounded" width="70%" height={18} />
                                    {index % 5 === 0 && (
                                        <Skeleton variant="rounded" width="55%" height={18} />
                                    )}
                                </Stack>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Stack>
        </Card>
    );
};
